import React, { useState, useEffect } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { useSelector, useDispatch } from 'react-redux';
import { updatePartnerProfile } from '../../api/partner';
import { UPDATE_PARTNER_INFO } from '../../features/partner/partnerSlice';
import { setPartnerInfo } from '../../utils/partnerInfo';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const NgoProfile = () => {
  const { setHeaderTitle } = useHeader();
  const dispatch = useDispatch();
  const { partnerInfo } = useSelector((state) => state.partner);

  // Basic NGO Information
  const [ngoData, setNgoData] = useState({
    ngoName: '',
    email: '',
    dateOfEstablishment: '',
    phone: '',
    branchAddress: '',
    country: '',
    state: '',
    city: '',
    headquarterAddress: '',
    websiteUrl: '',
    registration80G: '',
    registration12A: '',
    fcraStatus: '',
    taxExemptionCertificateDetails: ''
  });

  // Account Details
  const [accountData, setAccountData] = useState({
    username: '',
    displayName: '',
    email: ''
  });

  // Point of Contact
  const [pocData, setPocData] = useState({
    pocName: '',
    pocDesignation: '',
    pocDepartment: '',
    pocPhone: '',
    pocMobile: '',
    pocEmail: ''
  });

  // Social Media Links
  const [socialData, setSocialData] = useState({
    linkedin: '',
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
    website: ''
  });

  // Organizational Details
  const [orgDetails, setOrgDetails] = useState({
    visionAndObjective: '',
    keyFocusArea: '',
    keyProgram: '',
    keyProjects: '',
    fundingSources: '',
    activitiesToPromote: '',
    geographicScope: '',
    targetBeneficiaries: ''
  });

  // Partnership Details
  const [partnershipData, setPartnershipData] = useState({
    typesOfEngagement: [],
    partnershipStartDate: '',
    partnershipGoals: ''
  });

  // Tree Plantation Drives
  const [treePlantation, setTreePlantation] = useState({
    preferredLocation: '',
    frequency: '',
    pastDriveDetails: '',
    totalDrives: '',
    totalPlants: '',
    typeOfTrees: ''
  });

  // Food Distribution Drives
  const [foodDistribution, setFoodDistribution] = useState({
    preferredLocation: '',
    frequency: '',
    pastDriveDetails: '',
    totalAmountKgs: '',
    totalBeneficiaries: ''
  });

  // Events
  const [eventsData, setEventsData] = useState({
    typeOfEvents: '',
    frequency: '',
    preferredLocation: '',
    volunteerNeeds: '',
    pastEventDetails: ''
  });

  // Impact & Reporting
  const [impactData, setImpactData] = useState({
    metricsDataPoints: [],
    reportingFrequency: 'Quarterly',
    reportingGoals: '',
    remarksNotes: ''
  });

  // Account Status
  const [accountStatus, setAccountStatus] = useState({
    status: 'Active',
    lastUpdated: '',
    createdAt: ''
  });

  // Change Password
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    setHeaderTitle('Profile & Settings');

    if (partnerInfo) {
      // Populate Basic NGO Information
      setNgoData({
        ngoName: partnerInfo.ngoName || '',
        email: partnerInfo.email || '',
        dateOfEstablishment: partnerInfo.dateOfEstablishment || '',
        phone: partnerInfo.phone || '',
        branchAddress: partnerInfo.branchAddress || '',
        country: partnerInfo.country || '',
        state: partnerInfo.state || '',
        city: partnerInfo.city || '',
        headquarterAddress: partnerInfo.headquarterAddress || '',
        websiteUrl: partnerInfo.websiteUrl || '',
        registration80G: partnerInfo.registration80G || '',
        registration12A: partnerInfo.registration12A || '',
        fcraStatus: partnerInfo.fcraStatus || '',
        taxExemptionCertificateDetails: partnerInfo.taxExemptionCertificateDetails || ''
      });

      // Populate Account Details
      setAccountData({
        username: partnerInfo.accountDetails?.username || '',
        displayName: partnerInfo.accountDetails?.displayName || partnerInfo.ngoName || '',
        email: partnerInfo.accountDetails?.emails?.[0] || partnerInfo.email || ''
      });

      // Populate Point of Contact
      setPocData({
        pocName: partnerInfo.pointOfContact?.pocName || '',
        pocDesignation: partnerInfo.pointOfContact?.pocDesignation || '',
        pocDepartment: partnerInfo.pointOfContact?.pocDepartment || '',
        pocPhone: partnerInfo.pointOfContact?.pocPhone || '',
        pocMobile: partnerInfo.pointOfContact?.pocMobile || '',
        pocEmail: partnerInfo.pointOfContact?.pocEmail || ''
      });

      // Populate Social Media Links
      setSocialData({
        linkedin: partnerInfo.socialLinks?.linkedIn || '',
        facebook: partnerInfo.socialLinks?.facebook || '',
        twitter: partnerInfo.socialLinks?.twitter || '',
        instagram: partnerInfo.socialLinks?.instagram || '',
        youtube: partnerInfo.socialLinks?.youtube || '',
        website: partnerInfo.socialLinks?.website || ''
      });

      // Populate Organizational Details
      setOrgDetails({
        visionAndObjective: partnerInfo.organizationalDetails?.visionAndObjective || '',
        keyFocusArea: partnerInfo.organizationalDetails?.keyFocusArea || '',
        keyProgram: partnerInfo.organizationalDetails?.keyProgram || '',
        keyProjects: partnerInfo.organizationalDetails?.keyProjects || '',
        fundingSources: partnerInfo.organizationalDetails?.fundingSources || '',
        activitiesToPromote: partnerInfo.organizationalDetails?.activitiesToPromoteThroughThePartnership || '',
        geographicScope: partnerInfo.organizationalDetails?.geographicScopeOfOperations || '',
        targetBeneficiaries: partnerInfo.organizationalDetails?.targetBeneficiaries || ''
      });

      // Populate Partnership Details
      setPartnershipData({
        typesOfEngagement: partnerInfo.partnershipDetails?.typesOfEngagement || [],
        partnershipStartDate: partnerInfo.partnershipDetails?.partnershipStartDate || '',
        partnershipGoals: partnerInfo.partnershipDetails?.partnershipGoals || ''
      });

      // Populate Tree Plantation Drives
      setTreePlantation({
        preferredLocation: partnerInfo.treePlantationDrives?.treePlantationPreferredLocation || '',
        frequency: partnerInfo.treePlantationDrives?.treePlantationFrequency || '',
        pastDriveDetails: partnerInfo.treePlantationDrives?.treePlantationFewPastDriveDetails || '',
        totalDrives: partnerInfo.treePlantationDrives?.treePlantationTotalNumberOfDrives || '',
        totalPlants: partnerInfo.treePlantationDrives?.treePlantationTotalNumberOfPlants || '',
        typeOfTrees: partnerInfo.treePlantationDrives?.typeOfTreesPlanted || ''
      });

      // Populate Food Distribution Drives
      setFoodDistribution({
        preferredLocation: partnerInfo.foodDistributionDrives?.foodDistributionPreferredLocation || '',
        frequency: partnerInfo.foodDistributionDrives?.foodDistributionFrequency || '',
        pastDriveDetails: partnerInfo.foodDistributionDrives?.foodDistributionFewPastDriveDetails || '',
        totalAmountKgs: partnerInfo.foodDistributionDrives?.foodDistributionTotalNumberOfDrives || '',
        totalBeneficiaries: partnerInfo.foodDistributionDrives?.foodDistributionTotalNumberOfPlants || ''
      });

      // Populate Events
      setEventsData({
        typeOfEvents: partnerInfo.events?.typeOfEvents || '',
        frequency: partnerInfo.events?.eventsFrequency || '',
        preferredLocation: partnerInfo.events?.eventsPreferredLocation || '',
        volunteerNeeds: partnerInfo.events?.volunteerNeeds || '',
        pastEventDetails: partnerInfo.events?.detailsOfEvents || ''
      });

      // Populate Impact & Reporting
      setImpactData({
        metricsDataPoints: partnerInfo.impactAndReporting?.metricsOrDataPointsTheyPlanToTrack || [],
        reportingFrequency: partnerInfo.impactAndReporting?.reportingFrequency || 'Quarterly',
        reportingGoals: partnerInfo.impactAndReporting?.reportingGoals || '',
        remarksNotes: partnerInfo.impactAndReporting?.remarksOrNotes || ''
      });

      // Populate Account Status
      setAccountStatus({
        status: partnerInfo.active ? 'Active' : 'Inactive',
        lastUpdated: partnerInfo.updatedAt ? new Date(partnerInfo.updatedAt).toLocaleDateString() : '',
        createdAt: partnerInfo.createdAt ? new Date(partnerInfo.createdAt).toLocaleDateString() : ''
      });

      if (partnerInfo.logo) {
        setImagePreview(partnerInfo.logo);
      }
    }
  }, [setHeaderTitle, partnerInfo]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long!');
      return;
    }

    setLoading(true);
    try {
      // Call password change API
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.info('Profile updates are managed by administrators. Please contact support for changes.');
  };

  // Check if a specific engagement type is selected
  const hasEngagementType = (type) => {
    return partnershipData.typesOfEngagement.includes(type);
  };

  return (
    <div className='w-full p-6'>
      {/* Header Section with Profile Image */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
        <div className='p-6'>
          <div className='flex items-start gap-6'>
            <div className='flex-shrink-0'>
              <div className='w-20 h-20 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-2xl'>
                {imagePreview ? (
                  <img src={imagePreview} alt="Profile" className='w-20 h-20 rounded-full object-cover' />
                ) : (
                  <span>NGO</span>
                )}
              </div>
            </div>
            <div className='flex-grow'>
              <h2 className='text-xl font-semibold text-gray-900'>{accountData.displayName || ngoData.ngoName}</h2>
              <p className='text-sm text-gray-500 mt-1'>@{accountData.username}</p>
              <span className={`inline-block mt-2 px-3 py-1 ${accountStatus.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} text-xs font-semibold rounded`}>
                {accountStatus.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic NGO Information */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              NGO Information
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>NGO Name:</div>
                <div className='text-sm text-gray-900'>{ngoData.ngoName || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Email:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{ngoData.email || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Date of Establishment:</div>
                <div className='text-sm text-gray-900'>{ngoData.dateOfEstablishment ? new Date(ngoData.dateOfEstablishment).toLocaleDateString() : 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Phone:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{ngoData.phone || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Branch Address:</div>
                <div className='text-sm text-gray-500'>{ngoData.branchAddress || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Headquarter Address:</div>
                <div className='text-sm text-gray-500'>{ngoData.headquarterAddress || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Country:</div>
                <div className='text-sm text-gray-900'>{ngoData.country || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>State:</div>
                <div className='text-sm text-gray-900'>{ngoData.state || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>City:</div>
                <div className='text-sm text-gray-900'>{ngoData.city || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Website URL:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {ngoData.websiteUrl ? (
                    <a href={ngoData.websiteUrl} target="_blank" rel="noopener noreferrer">{ngoData.websiteUrl}</a>
                  ) : (
                    'Not provided'
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>80G Registration:</div>
                <div className='text-sm text-gray-500'>{ngoData.registration80G || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>12A Registration:</div>
                <div className='text-sm text-gray-500'>{ngoData.registration12A || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>FCRA Status:</div>
                <div className='text-sm text-gray-500'>{ngoData.fcraStatus || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Tax Exemption Certificate:</div>
                <div className='text-sm text-gray-500'>{ngoData.taxExemptionCertificateDetails || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Account Details
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Username:</div>
                <div className='text-sm text-gray-900'>{accountData.username || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Display Name:</div>
                <div className='text-sm text-gray-900'>{accountData.displayName || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Email Address:</div>
                <div className='text-sm text-gray-900'>{accountData.email || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Point of Contact */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Point of Contact
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>POC Name:</div>
                <div className='text-sm text-gray-900'>{pocData.pocName || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Phone:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{pocData.pocPhone || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Designation:</div>
                <div className='text-sm text-gray-900'>{pocData.pocDesignation || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Mobile:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{pocData.pocMobile || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Department:</div>
                <div className='text-sm text-gray-500'>{pocData.pocDepartment || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Email:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{pocData.pocEmail || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Social Media Links
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>LinkedIn:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.linkedin ? (
                    <a href={socialData.linkedin} target="_blank" rel="noopener noreferrer">View Profile</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Instagram:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.instagram ? (
                    <a href={socialData.instagram} target="_blank" rel="noopener noreferrer">View Profile</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Facebook:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.facebook ? (
                    <a href={socialData.facebook} target="_blank" rel="noopener noreferrer">View Profile</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>YouTube:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.youtube ? (
                    <a href={socialData.youtube} target="_blank" rel="noopener noreferrer">View Channel</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Twitter:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.twitter ? (
                    <a href={socialData.twitter} target="_blank" rel="noopener noreferrer">View Profile</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Website:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.website ? (
                    <a href={socialData.website} target="_blank" rel="noopener noreferrer">{socialData.website}</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organizational Details */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Organizational Details
            </h3>
            <div className='space-y-3'>
              <div className='grid grid-cols-[200px_1fr] gap-4'>
                <div className='text-sm text-gray-600'>Vision & Objective:</div>
                <div className='text-sm text-gray-900'>{orgDetails.visionAndObjective || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-[200px_1fr] gap-4'>
                <div className='text-sm text-gray-600'>Key Focus Area:</div>
                <div className='text-sm text-gray-900'>{orgDetails.keyFocusArea || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-[200px_1fr] gap-4'>
                <div className='text-sm text-gray-600'>Key Programs:</div>
                <div className='text-sm text-gray-900'>{orgDetails.keyProgram || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-[200px_1fr] gap-4'>
                <div className='text-sm text-gray-600'>Key Projects:</div>
                <div className='text-sm text-gray-900'>{orgDetails.keyProjects || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-[200px_1fr] gap-4'>
                <div className='text-sm text-gray-600'>Funding Sources:</div>
                <div className='text-sm text-gray-900'>{orgDetails.fundingSources || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-[200px_1fr] gap-4'>
                <div className='text-sm text-gray-600'>Activities to Promote:</div>
                <div className='text-sm text-gray-900'>{orgDetails.activitiesToPromote || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-[200px_1fr] gap-4'>
                <div className='text-sm text-gray-600'>Geographic Scope:</div>
                <div className='text-sm text-gray-900'>{orgDetails.geographicScope || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-[200px_1fr] gap-4'>
                <div className='text-sm text-gray-600'>Target Beneficiaries:</div>
                <div className='text-sm text-gray-900'>{orgDetails.targetBeneficiaries || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Partnership Details */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Partnership Details
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Types of Engagement:</div>
                <div className='text-sm text-gray-900'>{Array.isArray(partnershipData.typesOfEngagement) && partnershipData.typesOfEngagement.length > 0 ? partnershipData.typesOfEngagement.join(', ') : 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Partnership Start Date:</div>
                <div className='text-sm text-gray-900'>{partnershipData.partnershipStartDate ? new Date(partnershipData.partnershipStartDate).toLocaleDateString() : 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Partnership Goals:</div>
                <div className='text-sm text-gray-900'>{partnershipData.partnershipGoals || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tree Plantation Drives - Conditional */}
        {hasEngagementType('Tree Planting') && (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
            <div className='p-6'>
              <h3 className='text-base font-semibold text-gray-900 mb-4'>
                Tree Plantation Drives
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Preferred Location:</div>
                  <div className='text-sm text-gray-900'>{treePlantation.preferredLocation || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Frequency:</div>
                  <div className='text-sm text-gray-900'>{treePlantation.frequency || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Total Number of Drives:</div>
                  <div className='text-sm text-gray-900'>{treePlantation.totalDrives || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Total Plants:</div>
                  <div className='text-sm text-gray-900'>{treePlantation.totalPlants || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Type of Trees:</div>
                  <div className='text-sm text-gray-900'>{treePlantation.typeOfTrees || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Past Drive Details:</div>
                  <div className='text-sm text-gray-900'>{treePlantation.pastDriveDetails || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Food Distribution Drives - Conditional */}
        {hasEngagementType('Food Distribution') && (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
            <div className='p-6'>
              <h3 className='text-base font-semibold text-gray-900 mb-4'>
                Food Distribution Drives
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Preferred Location:</div>
                  <div className='text-sm text-gray-900'>{foodDistribution.preferredLocation || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Frequency:</div>
                  <div className='text-sm text-gray-900'>{foodDistribution.frequency || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Total Amount (KGs):</div>
                  <div className='text-sm text-gray-900'>{foodDistribution.totalAmountKgs || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Total Beneficiaries:</div>
                  <div className='text-sm text-gray-900'>{foodDistribution.totalBeneficiaries || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Past Drive Details:</div>
                  <div className='text-sm text-gray-900'>{foodDistribution.pastDriveDetails || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Events - Conditional */}
        {hasEngagementType('Event Promotion') && (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
            <div className='p-6'>
              <h3 className='text-base font-semibold text-gray-900 mb-4'>
                Events
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Type of Events:</div>
                  <div className='text-sm text-gray-900'>{eventsData.typeOfEvents || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Frequency:</div>
                  <div className='text-sm text-gray-900'>{eventsData.frequency || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Preferred Location:</div>
                  <div className='text-sm text-gray-900'>{eventsData.preferredLocation || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Volunteer Needs:</div>
                  <div className='text-sm text-gray-900'>{eventsData.volunteerNeeds || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Past Event Details:</div>
                  <div className='text-sm text-gray-900'>{eventsData.pastEventDetails || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Impact & Reporting */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Impact & Reporting
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Metrics Data Points:</div>
                <div className='text-sm text-gray-900'>{Array.isArray(impactData.metricsDataPoints) && impactData.metricsDataPoints.length > 0 ? impactData.metricsDataPoints.join(', ') : 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Reporting Frequency:</div>
                <div className='text-sm text-gray-900'>{impactData.reportingFrequency}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Reporting Goals:</div>
                <div className='text-sm text-gray-900'>{impactData.reportingGoals || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Remarks/Notes:</div>
                <div className='text-sm text-gray-900'>{impactData.remarksNotes || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Status */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Account Status
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Account Status:</div>
                <div className='text-sm'>
                  <span className={`inline-block px-3 py-1 ${accountStatus.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} text-xs font-semibold rounded`}>
                    {accountStatus.status}
                  </span>
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Last Updated:</div>
                <div className='text-sm text-gray-900'>{accountStatus.lastUpdated || 'Not available'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Created At:</div>
                <div className='text-sm text-gray-900'>{accountStatus.createdAt || 'Not available'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password section removed - use the one in the top-right dropdown menu instead */}
      </form>

      <ToastContainer
        position="bottom-right"
        autoClose={2000}
      />
    </div>
  );
};

export default NgoProfile;
